import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Alert, Badge, Icon, Modal } from '@syneto/compass-react';
import Utils from '../../../../helpers/Utils';
import { getApiErrorMessage } from '../../../../helpers/apiError';
import { computeAddonEndDate } from '../../../../helpers/addonPeriod';
import { getEditionGrants, getEditions } from '../../../../services/editions';
import { describeEditionLicenses } from '../Editions/editionDisplay';
import {
	DEFAULT_EDITION_REQUEST,
	DURATION_TYPE_OPTIONS,
	EDITION_OPTIONS,
	toEditionGrantRows,
	toEditionGrantsQuery,
	validateEditionRequest
} from '../../../../helpers/editionGrants';

// One card per edition, as there are two: the icon tells them apart at a glance.
const EDITION_ICONS = { standard: 'fa fa-cube', advanced: 'fa fa-cubes' };

/*
 * What the dialog is about to add, in one line under the fields: the number of licenses and the period, with the
 * last day they work when it can be told. Nothing until an edition is chosen.
 */
export const describeEditionRequest = (request, licenseCount) => {
	if (licenseCount === null) {
		return '';
	}

	const licenses = `Adds ${licenseCount} ${licenseCount === 1 ? 'license' : 'licenses'}`;
	const duration = request.duration ? `${request.duration} ${request.durationType}` : '';

	if (!duration) {
		return licenses;
	}
	if (request.startOnActivation) {
		return `${licenses} · ${duration} from the activation`;
	}
	if (!request.startDate) {
		return `${licenses} · ${duration}`;
	}

	const start = moment.utc(request.startDate);
	const end = computeAddonEndDate(request.startDate, request.duration, request.durationType);
	const from = start.isValid() ? start.format('DD/MM/YYYY') : request.startDate;

	return end ? `${licenses} · ${duration} from ${from}, ending ${end.format('DD/MM/YYYY')}` : `${licenses} · ${duration} from ${from}`;
};

/*
 * Asks for an edition, a start and a duration, previews the edition's grants through the server and hands the rows to
 * the product form. Nothing is written: the rows are saved with the product, or dropped with "Exit without saving".
 */
export const ApplyEditionModal = ({ show, onHide, onApply, catalog = [] }) => {
	const [request, setRequest] = useState(DEFAULT_EDITION_REQUEST);
	const [errors, setErrors] = useState({});
	const [serverError, setServerError] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [editions, setEditions] = useState([]);
	// The card under the pointer, so it answers the hover like a button: the stylesheet has no rule for these cards.
	const [hoveredCode, setHoveredCode] = useState(null);

	useEffect(() => {
		if (show) {
			setRequest(DEFAULT_EDITION_REQUEST);
			setErrors({});
			setServerError(null);
			setIsLoading(false);
		}
	}, [show]);

	/*
	 * The editions are read when the dialog opens, to show what the chosen one gives before it is applied. The list
	 * is a help, not a condition: when it cannot be read the dialog says nothing and Apply still works.
	 */
	useEffect(() => {
		if (!show) {
			return undefined;
		}

		let isCurrent = true;

		getEditions({ page: 1, pageSize: 100 })
			.then((response) => {
				if (isCurrent) {
					setEditions(response.data?.items ?? []);
				}
			})
			.catch(() => {
				if (isCurrent) {
					setEditions([]);
				}
			});

		return () => { isCurrent = false; };
	}, [show]);

	const selectedEdition = editions.find((edition) => { return edition.code === request.editionCode; });

	const update = (field, value) => {
		setRequest((current) => { return { ...current, [field]: value }; });
		setErrors((current) => { return { ...current, [field]: undefined }; });
	};

	const apply = () => {
		const validation = validateEditionRequest(request);
		setErrors(validation);

		if (Object.keys(validation).length > 0) {
			return;
		}

		setIsLoading(true);
		setServerError(null);
		getEditionGrants(toEditionGrantsQuery(request))
			.then((response) => {
				onApply(toEditionGrantRows(response.data?.items, Utils.uuidv4));
				setIsLoading(false);
				onHide();
			})
			.catch((error) => {
				setIsLoading(false);
				setServerError(getApiErrorMessage(error, 'The edition could not be applied.'));
			});
	};

	const feedback = (field) => {
		return errors[field] ? <div className="invalid-feedback d-block">{errors[field]}</div> : null;
	};

	const selectedOption = EDITION_OPTIONS.find((option) => { return option.value === request.editionCode; });
	const selectedLicenses = selectedEdition ? describeEditionLicenses(selectedEdition, catalog) : null;
	const summary = describeEditionRequest(request, selectedLicenses ? selectedLicenses.length : null);

	const content = (
		<div className="apply-edition">
			<p className="text-muted">The edition's licenses are added to the list with the tier the edition gives each. Multiple sockets is not part of an edition: add it with &quot;Add license&quot;.</p>
			{serverError && <Alert appearance="danger">{serverError}</Alert>}
			<span className="form-label d-block" id="apply-edition-code-label">Edition*</span>
			<div className="row g-3 mb-3" role="radiogroup" aria-labelledby="apply-edition-code-label">
				{EDITION_OPTIONS.map((option) => {
					const isSelected = request.editionCode === option.value;
					const isHovered = hoveredCode === option.value && !isLoading;
					const edition = editions.find((item) => { return item.code === option.value; });
					const licenses = edition ? describeEditionLicenses(edition, catalog) : [];

					return (
						<div key={option.value} className="col-6">
							<button
								type="button"
								id={`apply-edition-${option.value}`}
								className={`apply-edition__plan btn w-100 h-100 text-start p-3 border rounded-3 ${isSelected ? 'border-primary bg-light' : (isHovered ? 'border-primary bg-white' : 'bg-white')}`}
								style={isSelected ? { boxShadow: '0 0 0 3px rgba(91, 95, 207, 0.2)' } : undefined}
								role="radio"
								aria-checked={isSelected}
								disabled={isLoading}
								onClick={() => { update('editionCode', option.value); }}
								onMouseEnter={() => { setHoveredCode(option.value); }}
								onMouseLeave={() => { setHoveredCode(null); }}
							>
								<div className="d-flex align-items-center gap-2">
									<Icon name={EDITION_ICONS[option.value] ?? 'fa fa-cube'} className={isSelected ? 'text-primary' : 'text-muted'} />
									<span className="fw-bold fs-6 flex-grow-1">{edition?.name ?? option.label}</span>
									{/* The same circle on every card says it can be picked; the picked one is filled with a check. */}
									<span
										className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 border rounded-circle ${isSelected ? 'border-primary bg-primary text-white' : 'bg-white'}`}
										style={{ width: '20px', height: '20px', borderWidth: '2px', fontSize: '11px' }}
										aria-hidden="true"
									>
										{isSelected && <Icon name="fa fa-check" color="#fff" />}
									</span>
								</div>
								{edition?.description && <div className="text-muted small mt-1">{edition.description}</div>}
								{licenses.length > 0 && (
									<ul className="list-unstyled mb-0 mt-3 pt-3 border-top apply-edition__licenses">
										{licenses.map((license, index) => {
											return (
												<li key={`${license.licenseId}-${index}`} className="d-flex align-items-center justify-content-between gap-2 py-1 small">
													<span className="text-truncate">{license.licenseName}</span>
													<Badge appearance="secondary">{license.tierName}</Badge>
												</li>
											);
										})}
									</ul>
								)}
							</button>
						</div>
					);
				})}
			</div>
			{feedback('editionCode')}
			<div className="row align-items-end">
				<div className="col-4 mb-3">
					<label className="form-label" htmlFor="apply-edition-start">Start date{request.startOnActivation ? '' : '*'}</label>
					<input id="apply-edition-start" type="date" className={`form-control ${errors.startDate ? 'is-invalid' : ''}`} value={request.startDate} onChange={(event) => { update('startDate', event.target.value); }} disabled={isLoading || request.startOnActivation} />
					{feedback('startDate')}
				</div>
				<div className="col-3 mb-3">
					<div className="form-check mb-2">
						<input id="apply-edition-start-on-activation" type="checkbox" className="form-check-input" checked={request.startOnActivation} onChange={(event) => { update('startOnActivation', event.target.checked); }} disabled={isLoading} />
						<label className="form-check-label" htmlFor="apply-edition-start-on-activation">Start on activation</label>
					</div>
				</div>
				<div className="col-5 mb-3">
					{/* The unit is the duration's suffix, "12 Months" under one label, as it is read. */}
					<label className="form-label" htmlFor="apply-edition-duration">Duration*</label>
					<div className="d-flex gap-2">
						<input id="apply-edition-duration" type="number" min="1" step="1" className={`form-control ${errors.duration ? 'is-invalid' : ''}`} style={{ flex: '0 0 40%' }} value={request.duration} onChange={(event) => { update('duration', event.target.value); }} disabled={isLoading} />
						<select id="apply-edition-duration-type" className="form-select" aria-label="Duration unit" value={request.durationType} onChange={(event) => { update('durationType', event.target.value); }} disabled={isLoading}>
							{DURATION_TYPE_OPTIONS.map((option) => {
								return <option key={option.value} value={option.value}>{option.label}</option>;
							})}
						</select>
					</div>
					{feedback('duration')}
					{feedback('durationType')}
				</div>
			</div>
			{summary && (
				<p className="text-muted mb-0 d-flex align-items-center gap-2 apply-edition__summary">
					{/* The licenses stylesheet enlarges every info icon on the page; this one stays the size of the text. */}
					<Icon name="fa fa-info-circle" style={{ fontSize: '16px', marginLeft: 0 }} />
					<span>{summary}</span>
				</p>
			)}
		</div>
	);

	return (
		<Modal
			enforceFocus={false}
			show={show}
			onHide={onHide}
			size="lg"
			title="Apply edition"
			content={content}
			actions={{
				primary: {
					label: isLoading ? 'Applying…' : (selectedOption ? `Apply ${selectedOption.label}` : 'Apply'),
					onClick: apply,
					disabled: isLoading
				},
				secondary: {
					label: 'Cancel',
					onClick: onHide,
					disabled: isLoading
				}
			}}
		/>
	);
};

ApplyEditionModal.propTypes = {
	show: PropTypes.bool.isRequired,
	onHide: PropTypes.func.isRequired,
	onApply: PropTypes.func.isRequired,
	// The license catalog (`GET /licensing/addons`), which names the licenses and tiers an edition gives.
	catalog: PropTypes.array
};

export default ApplyEditionModal;
